import { retryBusy } from "../nest/appDataRetry"
import { requestNestMethod, type NestMethodParams, type NestMethodResult } from "../nest/client"
import {
    createMcpServerRecord,
    createMcpServerStore,
    MCP_SERVERS_KEY,
    MCP_SERVERS_NAMESPACE,
    parseMcpServerRecord,
    type McpServerItem,
    type McpServerStore,
} from "./mcpServerStore"

type McpServerMethod = "appData.get" | "appData.set"
export type McpServerRequest = <M extends McpServerMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface McpServerStoreConnection {
    store: McpServerStore
    sync: () => Promise<void>
    disconnect: () => void
}

interface McpServerStoreOptions {
    request?: McpServerRequest
    now?: () => string
}

const defaultRequest: McpServerRequest = (method, params) => requestNestMethod(method, params)

export async function connectMcpServerStore(options: McpServerStoreOptions = {}): Promise<McpServerStoreConnection> {
    const request = options.request ?? defaultRequest
    const now = options.now ?? (() => new Date().toISOString())
    let disposed = false
    let dirty = false
    let latestServers: McpServerItem[] = []

    const result = await retryBusy(() =>
        request("appData.get", {
            namespace: MCP_SERVERS_NAMESPACE,
            key: MCP_SERVERS_KEY,
        })
    )
    const parsed = result.found ? parseMcpServerRecord(result.value) : null
    latestServers = parsed?.servers ?? []

    const store = createMcpServerStore(parsed ?? createMcpServerRecord([], now()), (servers) => {
        latestServers = servers
        dirty = true
        if (!disposed) void sync()
    })

    async function sync(): Promise<void> {
        if (!dirty) return
        dirty = false
        try {
            await retryBusy(() =>
                request("appData.set", {
                    namespace: MCP_SERVERS_NAMESPACE,
                    key: MCP_SERVERS_KEY,
                    value: createMcpServerRecord(latestServers, now()),
                })
            )
        } catch (error) {
            dirty = true
            throw error
        }
    }

    return {
        store,
        sync,
        disconnect: () => {
            disposed = true
        },
    }
}
